import { useCallback, useEffect, useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { convertToUsdString } from 'src/app/shared/utils/currency';

import { useRealtimeValidation } from '../../../../../../container/hooks/useRealtimeValidation';
import { useMembershipModalState } from '../../../../../../modalContext';

type Result = ModelHookResult<
	{
		buttonAmounts: number[];
		selection: number;
		currency: string;
		currencySymbol: string;
		errorMessage: string | null;
		placeholder: string;
		customAmount: string;
		amountButtonProps: {
			currency: string;
			isSunriseEnabled: boolean;
			height: '56px';
			textSize: 'default';
			fontWeight: 'normal';
			borderWidth: string;
			borderWidthUnselected: '1px';
			color: 'sunrise.semantic.typography.accent.primary';
			colorUnselected: 'sunrise.semantic.typography.accent.primary';
			borderColorUnselected: 'sunrise.semantic.fill.accent.primary.strong';
			borderColor: 'sunrise.semantic.fill.accent.primary.strong';
			backgroundColorUnselected: 'sunrise.semantic.fill.inverse.strong';
			backgroundColor: string;
			hideOutlineWhenSelected: boolean;
		};
	},
	{
		handleAmountChange: (value: number) => void;
		handleCustomAmountFocus: () => void;
		handleCustomAmountChange: (value: string) => void;
	}
>;

// eslint-disable-next-line max-lines-per-function
export function useMultipleAmountSelector(): Result {
	const [
		{
			amount,
			minAmount,
			currencyCode,
			validationErrors,
			revenuePpmuMultiplePricesExpData,
			acceptMembership,
			currencyExchangeRate,
		},
		{ setAmount },
	] = useMembershipModalState();
	const { getCurrencySymbol, translate } = useI18n();
	const track = useTracking();
	const toAmountUsd = useCallback(
		(amt: number) => convertToUsdString(amt, currencyExchangeRate),
		[currencyExchangeRate],
	);

	const [isCustomAmount, setIsCustomAmount] = useState<boolean>(false);
	const [customAmount, setCustomAmount] = useState<number>(0);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const initialAmount = useMemo(() => amount, []);

	useRealtimeValidation();

	const amountChoices = useMemo(() => {
		const baseAmounts = revenuePpmuMultiplePricesExpData.amountChoices ?? [];

		const amountsToInclude = baseAmounts.includes(initialAmount)
			? [...baseAmounts, minAmount]
			: [...baseAmounts, initialAmount];

		const uniqueAmounts = Array.from(new Set(amountsToInclude));
		return uniqueAmounts.sort((a, b) => a - b);
	}, [revenuePpmuMultiplePricesExpData.amountChoices, initialAmount, minAmount]);

	const initialAmountPosition = useMemo(() => {
		return amountChoices.indexOf(initialAmount);
	}, [amountChoices, initialAmount]);

	const selection = useMemo(() => {
		if (isCustomAmount) return Number.NaN;
		return amountChoices.includes(amount) ? amount : Number.NaN;
	}, [amount, amountChoices, isCustomAmount]);

	const amountButtonProps = useMemo(
		() => ({
			currency: currencyCode,
			isSunriseEnabled: true,
			height: '56px' as const,
			textSize: 'default' as const,
			fontWeight: 'normal' as const,
			borderWidth: acceptMembership ? '2px' : '1px',
			borderWidthUnselected: '1px' as const,
			color: 'sunrise.semantic.typography.accent.primary' as const,
			colorUnselected: 'sunrise.semantic.typography.accent.primary' as const,
			borderColorUnselected: 'sunrise.semantic.fill.accent.primary.strong' as const,
			borderColor: 'sunrise.semantic.fill.accent.primary.strong' as const,
			backgroundColorUnselected: 'sunrise.semantic.fill.inverse.strong' as const,
			backgroundColor: acceptMembership
				? 'sunrise.semantic.fill.accent.primary.weaker'
				: 'sunrise.semantic.fill.inverse.strong',
			hideOutlineWhenSelected: true,
		}),
		[currencyCode, acceptMembership],
	);

	useEffect(() => {
		void track('membership_ask_multiple_buttons_selector_viewed', {
			local_amount: initialAmount,
			amount: toAmountUsd(initialAmount),
			initial_amount_position: initialAmountPosition,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		data: {
			buttonAmounts: amountChoices,
			selection,
			currency: currencyCode,
			currencySymbol: getCurrencySymbol(currencyCode),
			errorMessage: isCustomAmount && customAmount !== 0 ? (validationErrors?.amount ?? null) : null,
			placeholder: translate('corgi.co.promote.custom_amount'),
			customAmount: isCustomAmount && customAmount !== 0 ? String(customAmount) : '',
			amountButtonProps,
		},
		actions: {
			handleAmountChange: useCallback(
				(value: number) => {
					setAmount(value);
					setCustomAmount(0);
					setIsCustomAmount(false);
					void track('membership_ask_multiple_buttons_amount_changed', {
						amount: toAmountUsd(value),
						local_amount: value,
						initial_amount_position: initialAmountPosition,
					});
				},
				[setAmount, track, toAmountUsd, initialAmountPosition],
			),
			handleCustomAmountFocus: useCallback(() => {
				setIsCustomAmount(true);
				setCustomAmount(customAmount);
				void track('membership_ask_multiple_buttons_amount_focused', {
					local_amount: customAmount,
					amount: toAmountUsd(customAmount),
					initial_amount_position: initialAmountPosition,
				});
			}, [customAmount, track, initialAmountPosition, toAmountUsd]),
			handleCustomAmountChange: useCallback(
				(value: string) => {
					if (/^\d*$/.test(value)) {
						const newAmount = Number(value);
						setAmount(newAmount);

						setCustomAmount(newAmount);
						void track('membership_ask_multiple_buttons_amount_input_changed', {
							amount: toAmountUsd(newAmount),
							local_amount: newAmount,
						});
					}
				},
				[setAmount, track, toAmountUsd],
			),
		},
	};
}
